import type { UseFormReturn } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared-kernel/primary/shared/shadcn/ui/dialog";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { BillingAddressField } from "@shared-kernel/primary/shared/billing-address-field";
import { ReadOnlyField } from "@shared-kernel/primary/shared/shadcn/ui/read-only-field";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Loader2 } from "lucide-react";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { formatIban, formatToCurrency } from "@utils/formatting";
import { ORGANIZATION_NAME } from "@shared-kernel/domain/organization";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PopoverCalendar } from "@shared-kernel/primary/shared/calendar/popover-calendar";
import { getMinFiscalPeriodDate } from "@shared-kernel/primary/shared/date/min-fiscal-period-date";
import type { TeacherVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { RequestState } from "@redux/state/shared-types";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { PayoutRequestFormInputs } from "./form-validation/payout-request.schema";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hours: number;
  amount: number;
  profile: TeacherVM;
  now: Date;
  form: UseFormReturn<PayoutRequestFormInputs>;
  uploadProcessing: RequestState;
  onSubmit: FormSubmit;
  onUpload: (file: File) => void;
}

export const PayoutRequestDialog = ({
  open,
  onOpenChange,
  hours,
  amount,
  profile,
  uploadProcessing,
  now,
  form,
  onSubmit,
  onUpload,
}: Props) => {
  const {
    watch,
    control,
    formState: { isSubmitting },
  } = form;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Demande de règlement</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-6">
            <Alert variant="error">
              <p className="text-sm">
                ATTENTION : Avant d'envoyer votre facture, vérifiez que le montant total correspond exactement à vos heures déclarées. En
                cas d'erreur, dirigez-vous vers la page des cours pour effectuer les corrections nécessaires{" "}
                <strong>avant de demander le règlement</strong>.
              </p>
              <p className="mt-2 text-sm font-bold">
                Merci d'émettre la facture au nom de {ORGANIZATION_NAME[profile.organization].long} et non de l'élève car nous ne pouvons
                pas payer une facture qui ne nous est pas adressée.
              </p>
            </Alert>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <BillingAddressField organization={profile.organization} />

                <ReadOnlyField label="IBAN de paiement">
                  {profile.iban ? <span>{formatIban(profile.iban)}</span> : <span className="text-muted-foreground">Non renseigné</span>}
                </ReadOnlyField>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <ReadOnlyField label="Nombre d'heures" inline>
                  {hours} heure(s)
                </ReadOnlyField>
                <ReadOnlyField label="Montant total" inline>
                  {formatToCurrency(amount)}
                </ReadOnlyField>
                <FormField
                  control={control}
                  name="invoiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Numéro de facture</FormLabel>
                      <FormControl>
                        <Input placeholder="Exemple: F-2024-001" maxLength={100} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="invoiceDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date de facturation</FormLabel>
                      <FormControl>
                        <PopoverCalendar
                          now={now}
                          value={field.value}
                          onChange={field.onChange}
                          minDate={getMinFiscalPeriodDate(now)}
                          maxDate={now}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="invoiceDeadline"
                  render={({ field }) => {
                    const invoiceDate = watch("invoiceDate");
                    return (
                      <FormItem>
                        <FormLabel>Date d'échéance</FormLabel>
                        <FormControl>
                          <PopoverCalendar
                            now={now}
                            value={field.value}
                            onChange={field.onChange}
                            minDate={invoiceDate ? new Date(invoiceDate.getTime() + 86400000) : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              <FormField
                control={control}
                name="invoiceUrl"
                render={({ fieldState: { error } }) => (
                  <FormItem>
                    <UploadButton
                      onFileSelect={onUpload}
                      accept={["application/pdf"]}
                      label="Télécharger votre facture (PDF) (obligatoire)"
                      helpText="Format accepté: PDF uniquement"
                      maxSize={10 * 1024 * 1024}
                      value={watch("invoiceUrl")}
                      showPreview
                      processing={uploadProcessing}
                      error={error?.message}
                    />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="isConfirmed"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm">
                        Je confirme que le montant de ma facture s'élève à {formatToCurrency(amount)} et que ma facture est bien adressée à{" "}
                        {ORGANIZATION_NAME[profile.organization].long}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button className="w-full sm:w-auto" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  `Demander le règlement de ${formatToCurrency(amount)} (${hours}h)`
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
